import { useParams, Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Calendar, Clock, ArrowLeft, BookOpen, Info, AlertTriangle, Lightbulb } from 'lucide-react'
import { MarketingLayout } from '../components/MarketingLayout'
import { SEO } from '../components/SEO'
import { CTABanner } from '../components/CTABanner'
import { getArticleBySlug, type ArticleSection } from '../data/articles'
import '../styles/marketing-article.css'

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

function renderSection(section: ArticleSection, idx: number) {
  switch (section.type) {
    case 'heading': {
      if (section.level === 3) {
        return <h3 key={idx}>{section.content as string}</h3>
      }
      return <h2 key={idx}>{section.content as string}</h2>
    }
    case 'paragraph':
      return <p key={idx}>{section.content as string}</p>
    case 'list':
      return (
        <ul key={idx}>
          {(section.content as string[]).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )
    case 'code':
      return (
        <pre key={idx} className="marketing-article-code">
          <code>{section.content as string}</code>
        </pre>
      )
    case 'callout': {
      const Icon =
        section.variant === 'tip' ? Lightbulb :
        section.variant === 'warn' ? AlertTriangle :
        Info
      return (
        <div key={idx} className={`marketing-article-callout marketing-article-callout--${section.variant ?? 'info'}`}>
          <Icon size={18} className="marketing-article-callout-icon" />
          <p>{section.content as string}</p>
        </div>
      )
    }
    case 'video':
      return (
        <div key={idx} className="marketing-article-video">
          <video controls preload="metadata" style={{ width: '100%', borderRadius: 8 }}>
            <source src={section.content as string} />
            Tu navegador no soporta la reproducción de video.
          </video>
        </div>
      )
    default:
      return null
  }
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const article = slug ? getArticleBySlug(slug) : undefined

  if (!article) {
    return (
      <MarketingLayout>
        <SEO
          title="Artículo no encontrado"
          description="El artículo que buscas no existe o ha sido movido."
          canonical={`https://voltary.org/blog/${slug ?? ''}`}
        />
        <article className="marketing-article">
          <span className="marketing-article-eyebrow">404</span>
          <h1 className="marketing-article-title">Artículo no encontrado</h1>
          <p className="marketing-article-lead">
            El artículo que buscas no existe o ha sido movido. Vuelve al blog
            para ver todos los recursos disponibles.
          </p>
          <Link to="/blog" className="marketing-article-back">
            <ArrowLeft size={14} /> Volver al blog
          </Link>
        </article>
      </MarketingLayout>
    )
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: { '@type': 'Organization', name: article.author },
    publisher: { '@type': 'Organization', name: APP_NAME },
    mainEntityOfPage: `https://voltary.org/blog/${article.slug}`,
    inLanguage: 'es-ES',
  }

  return (
    <MarketingLayout>
      <SEO
        title={`${article.title} | ${APP_NAME}`}
        description={article.description}
        canonical={`https://voltary.org/blog/${article.slug}`}
        type="article"
        jsonLd={jsonLd}
      />

      <article className="marketing-article">
        <Link to="/blog" className="marketing-article-back">
          <ArrowLeft size={14} /> Volver al blog
        </Link>

        <motion.span
          className="marketing-article-eyebrow"
          style={{marginLeft: '1.5rem'}}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <BookOpen size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {article.category}
        </motion.span>

        <motion.h1
          className="marketing-article-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {article.title}
        </motion.h1>

        <motion.div
          className="marketing-article-meta"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <span className="marketing-article-author">{article.author}</span>
          <span><Calendar size={12} /> {formatDate(article.date)}</span>
          <span><Clock size={12} /> {article.readMinutes} min de lectura</span>
        </motion.div>

        <motion.p
          className="marketing-article-lead"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {article.description}
        </motion.p>

        <div className="marketing-article-body">
          {article.body.map((section, idx) => renderSection(section, idx))}
        </div>
      </article>

      <CTABanner
        title="¿Listo para crear tu servidor?"
        description="Después de leer, monta tu servidor en menos de 2 minutos con crédito gratis."
        primaryLabel="Empezar ahora"
        primaryHref="/login"
      />
    </MarketingLayout>
  )
}